import React, { useEffect, useRef, useState } from "react";
import { getOAuthHeaders } from "../../constants/oAuthValidation";
import { posOrderProcessor1, salesHistoryAggregator, upsertOfdOrderStatus, posAggregatorItemModify, generateAggregatorBill } from "../../services/index";
import { acknowledgeOrderNotification } from "../../services/orderServices";
import { getSalesHistoryDetails } from "../../utils/salesHistoryUtils";
import { checkAggregatorItemsStock, hasStockIssue, isItemOutOfStock } from "../../utils/aggregatorStockCheck";
import PrintController from "../../lib/printer/printController";
import CWKOTPrinter from "../../lib/printer/CWKotPrinter";
import { useHistory } from "react-router-dom";
import { message } from "antd";
import db from "../../database";
import Header from "./Header";
import OrderList from "./OrderList";
import OrderDetails from "./OrderDetails";
import ActionButtons from "./ActionButtons";
import ModifyItemsModal from "./ModifyItemsModal";
import messages from "../../errors/messages.en-IN.json";
import useAuthStore from "../../stores/useAuthStore";
import { getCurrentTillId } from "../../utils/tillIdentity";
import usePOSConfigStore from "../../stores/usePOSConfigStore";
import useSessionStore from "../../stores/useSessionStore";
import dayjs from "dayjs";
import storage from "../../utils/storage";

// BUG-2373: retire the order's pending-popup notification (cwr_pos_notification)
// when this screen decides it. Scoped by till_ref = cwrTill.till, the same id
// WebSocketProvider polls/acknowledges with (verified on GPR live: till_ref
// "MYP - E1"). Fire-and-forget and idempotent -- never blocks the status update.
const acknowledgeAggregatorNotification = (sOrderID) => {
  try {
    const tillRef = useAuthStore.getState().tillData?.tillAccess?.cwrTill?.till;
    if (!tillRef || !sOrderID) return;
    Promise.resolve(acknowledgeOrderNotification(tillRef, sOrderID)).catch((err) => console.error("[Aggregator] Acknowledge notification failed:", err));
  } catch (err) {
    console.error("[Aggregator] Acknowledge notification failed:", err);
  }
};

// BUG-2163: substitutedLineIds only lived in React state, which is wiped on
// any page reload/remount -- a line replaced before a refresh permanently
// lost its "still replaceable" status even though the order itself was
// unchanged. Persisted per-order via the repo's typed localStorage wrapper
// so it survives a reload; cleared once the order is Dispatched/Cancelled.
const substitutedLinesKey = (sOrderID) => `aggregatorSubstitutedLines_${sOrderID}`;

const Aggregator = ({ orderUpdateFlag }) => {
  const history = useHistory();
  const posConfig = usePOSConfigStore.getState().posConfig;
  const backRoute = posConfig?.enableEstimate === "Y" ? "/estimate" : "/pos";
  // TSK-3229: mirrors the backend's own gate (cwr_pos_config.config_json ->
  // allowModificationInAggregator) — off/absent means every path here is a
  // no-op, same as before this feature existed.
  const allowModification = posConfig?.allowModificationInAggregator === "Y";
  // TSK-4875: onDispatch (default) / onAccept / onBill. Missing key keeps
  // today's flow.
  let shipmentMode = posConfig?.aggregatorShipmentMode;
  if (!shipmentMode) {
    try {
      shipmentMode = JSON.parse(localStorage.getItem("posConfig"))?.aggregatorShipmentMode;
    } catch (e) {
      shipmentMode = null;
    }
  }
  shipmentMode = shipmentMode || "onDispatch";
  // TSK-4875: "detailed" / "simple". Missing or any other value -> null, which
  // keeps today's tabs exactly (cancelled orders stay hidden).
  let tabLayout = posConfig?.aggregatorTabLayout;
  if (!tabLayout) {
    try {
      tabLayout = JSON.parse(localStorage.getItem("posConfig"))?.aggregatorTabLayout;
    } catch (e) {
      tabLayout = null;
    }
  }
  tabLayout = tabLayout === "detailed" || tabLayout === "simple" ? tabLayout : null;
  // Neither layout has a cancelled tab, so cancelled orders stay hidden.
  const listedStatuses = ["NEW", "AK", "FR", "DI", "CO"];
  // TSK-4875 (design doc section 6): statuses Modify Items is offered on, per
  // shipment mode -- the window closes when the shipment is created. onAccept
  // ships on Accept, so only NEW; onBill ships on Generate Bill, so NEW/AK/FR;
  // onDispatch (and any unknown value) keeps today's AK/FR.
  const MODIFY_STATUSES_BY_MODE = { onAccept: ["NEW"], onBill: ["NEW", "AK", "FR"] };
  const modifyStatuses = MODIFY_STATUSES_BY_MODE[shipmentMode] || ["AK", "FR"];
  const handleBack = () => history.push(backRoute);
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedOrderToPrint, setSelectedOrderToPrint] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState("All Channels");
  const [searchValue, setSearchValue] = useState("");
  const [filteredOrdersState, setFilteredOrdersState] = useState([]);
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);
  const [stockMap, setStockMap] = useState({});
  const [oosOrderIds, setOosOrderIds] = useState(new Set());
  // TSK-3793: the order-level modify verdict from getAggregatorOrderStock
  // (modifiable "Y"/"N" + reason) -- "N" once the order is shipped / invoiced /
  // dispatched / cancelled. Optimistic "Y" default so "Modify Items" doesn't
  // flicker in on every order open; the async check hides it when the backend
  // says the order can no longer be modified.
  const [orderModifiable, setOrderModifiable] = useState("Y");
  // BUG-2163: order-lines already swapped once via posAggregatorItemModify --
  // kept replaceable even after the new product's own availabilityStatus
  // comes back "Yes", since a second swap here is driven by customer
  // preference, not stock. Reset whenever a different order is opened.
  const [substitutedLineIds, setSubstitutedLineIds] = useState(new Set());
  const [modifyItemsVisible, setModifyItemsVisible] = useState(false);
  const [confirmingModify, setConfirmingModify] = useState(false);
  const [generatingBill, setGeneratingBill] = useState(false);
  // BUG-2406: the 30s poll is scheduled once on mount, so it must call the
  // latest getSalesHistoryData through this ref -- calling the mount-time copy
  // saw selectedOrder as null forever and re-selected the newest order on
  // every tick, pulling the operator off the order they were modifying.
  const getSalesHistoryDataRef = useRef(null);
  const authHeaders = getOAuthHeaders();
  const tillValue = useAuthStore.getState().tillValue;
  const tillSession = useSessionStore.getState().tillSession;
  const disableAggregatorInvoicePrint = process.env.REACT_APP_DISABLE_AGGREGATOR_INVOICE_PRINT === "true";
  const disableAggregatorKOTPrint = process.env.REACT_APP_DISABLE_AGGREGATOR_KOT_PRINT === "true";

  const statusDisplayMapping = {
    NEW: "New",
    AK: "Acknowledged",
    FR: "Food Ready",
    DI: "Dispatched",
    CC: "Cancelled",
    CO: "Completed",
  };

  useEffect(() => {
    const interval = setInterval(() => setCurrentDateTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let isMounted = true;
    let timeoutId = null;

    const fetchDataWithInterval = async () => {
      if (!isMounted) return;

      await getSalesHistoryDataRef.current?.();

      // Schedule the next fetch only after the current one completes
      timeoutId = setTimeout(fetchDataWithInterval, 30000); // 30 seconds
    };

    // Initial fetch
    fetchDataWithInterval();

    // Cleanup on unmount
    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    if (searchValue) {
      const filtered = orders.filter(
        (order) => order.orderNo.toLowerCase().includes(searchValue.toLowerCase()) || order?.externalId.toLowerCase().includes(searchValue.toLowerCase())
      );
      setFilteredOrdersState(filtered);
    } else {
      setFilteredOrdersState(orders);
    }
  }, [searchValue, orders]);

  useEffect(() => {
    const STOCK_CHECK_STATUSES = ["NEW", "AK", "FR"];
    if (!allowModification || !selectedOrder || !STOCK_CHECK_STATUSES.includes(selectedOrder.status) || !selectedOrder.items?.length) {
      setStockMap({});
      setOrderModifiable("Y");
      return;
    }
    let cancelled = false;
    checkAggregatorItemsStock(getCurrentTillId(), selectedOrder.sOrderID).then(({ stockMap: result, modifiable }) => {
      if (cancelled) return;
      setStockMap(result);
      // TSK-3793: hide "Modify Items" entirely once the backend verdict is "N"
      // (order shipped / invoiced / dispatched / cancelled), instead of showing
      // it and letting the commit fail with a 409.
      setOrderModifiable(modifiable === "N" ? "N" : "Y");
      const hasOos = selectedOrder.items.some((item) => hasStockIssue(item, result));
      setOosOrderIds((prev) => {
        const next = new Set(prev);
        if (hasOos) next.add(selectedOrder.sOrderID);
        else next.delete(selectedOrder.sOrderID);
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [selectedOrder?.sOrderID, selectedOrder?.status]);

  // BUG-2163: hydrate substitution history for whichever order is selected
  // from localStorage, so it survives a page reload -- not just resets to
  // empty on every order switch.
  useEffect(() => {
    if (!selectedOrder?.sOrderID) {
      setSubstitutedLineIds(new Set());
      return;
    }
    const stored = storage.get(substitutedLinesKey(selectedOrder.sOrderID), []);
    setSubstitutedLineIds(new Set(Array.isArray(stored) ? stored : []));
  }, [selectedOrder?.sOrderID]);

  const handleModifyItems = () => setModifyItemsVisible(true);

  const handleConfirmItemModification = async (lineChanges, newLines) => {
    if (!selectedOrder || (!lineChanges?.length && !newLines?.length)) return;
    setConfirmingModify(true);
    try {
      // TSK-4127: only pass newLines when there's actually something in it, so
      // a REPLACE-only save (the pre-TSK-4127 path) calls this exactly as it
      // always did — no trailing empty-array argument.
      const response = newLines?.length
        ? await posAggregatorItemModify(getCurrentTillId(), selectedOrder.sOrderID, lineChanges, newLines)
        : await posAggregatorItemModify(getCurrentTillId(), selectedOrder.sOrderID, lineChanges);
      const result = response?.data?.data?.posAggregatorItemModify;
      if (result?.status === "200") {
        message.success(result?.message || "Order items updated successfully");
        setModifyItemsVisible(false);
        // BUG-2163: remember every REPLACE'd line so it stays flagged even
        // once its new product reports back as in stock -- persisted so a
        // later page reload doesn't lose this order's substitution history.
        // TSK-4127: QTY_EDIT lines are deliberately not added here — their
        // reduced qty is refetched and re-checked against stock like any
        // other line, no "stays flagged" override needed.
        const nextSubstitutedIds = new Set(substitutedLineIds);
        (lineChanges || []).forEach((lc) => {
          if (lc.changeType !== "QTY_EDIT") nextSubstitutedIds.add(lc.sOrderlineId);
        });
        setSubstitutedLineIds(nextSubstitutedIds);
        storage.set(substitutedLinesKey(selectedOrder.sOrderID), Array.from(nextSubstitutedIds));
        // BUG-2149: manualprint=true so selectedOrderToPrint (what handlePrint actually
        // prints) is refreshed alongside selectedOrder — otherwise a reprint after a
        // substitution still shows the pre-modification item (see getSalesHistoryDetails's
        // forPrinting/manualprint return-shape ternary in salesHistoryUtils.js).
        const data = await getSalesHistoryDetails(selectedOrder.sOrderID, selectedOrder.status, false, null, true);
        if (data?.detailedOrder) {
          setSelectedOrder(data.detailedOrder);
          // BUG-2163: stockMap was only ever recomputed on sOrderID/status
          // change, so it went stale the moment a substitution committed --
          // re-check it now so other still-unresolved OOS lines (and this
          // one, if it goes OOS again later) reflect current availability.
          const { stockMap: refreshedStockMap, modifiable: refreshedModifiable } = await checkAggregatorItemsStock(getCurrentTillId(), data.detailedOrder.sOrderID);
          setStockMap(refreshedStockMap);
          setOrderModifiable(refreshedModifiable === "N" ? "N" : "Y");
          const hasOos = data.detailedOrder.items?.some((item) => hasStockIssue(item, refreshedStockMap) || nextSubstitutedIds.has(item.sOrderlineID));
          setOosOrderIds((prev) => {
            const next = new Set(prev);
            if (hasOos) next.add(data.detailedOrder.sOrderID);
            else next.delete(data.detailedOrder.sOrderID);
            return next;
          });
        }
        if (data?.transformedOrder) setSelectedOrderToPrint(data.transformedOrder);
      } else {
        message.error(result?.message || "Failed to modify order item.");
      }
    } catch (error) {
      console.error("Error modifying order item:", error);
      message.error("Failed to modify order item.");
    } finally {
      setConfirmingModify(false);
    }
  };

  const getSalesHistoryData = async () => {
    const sortOptions = { documentno: "desc", dateordered: "desc", customer: null, totalAmount: null };
    const filteredSortOptions = Object.fromEntries(Object.entries(sortOptions).filter(([_, value]) => value !== null));
    const startDate = dayjs().format("YYYY-MM-DD");
    const endDate = dayjs().format("YYYY-MM-DD");
    const filterbyValues = {
      DateRange: [startDate, endDate],
    };
    const filterbyData = JSON.stringify(filterbyValues).replace(/"/g, '\\"');
    const sortData = JSON.stringify(filteredSortOptions).replace(/"/g, '\\"');
    setLoading(true);
    try {
      // Heal cwr_till_id at the source instead of trusting tillValue directly —
      // see getCurrentTillId's comment in utils/tillIdentity.js. salesHistoryAggregator
      // still takes the full tillValue-shaped object (its query only reads
      // .cwr_till_id off it), so only that field needs overriding.
      const response = await salesHistoryAggregator(sortData, filterbyData, { ...tillValue, cwr_till_id: getCurrentTillId() });
      const salesDataArray = response?.data?.data?.salesHistory || [];
      if (salesDataArray.length > 0) {
        const newOrders = salesDataArray
          .filter((salesData) => listedStatuses.includes(salesData.ofdStatus))
          .map((salesData) => ({
            ...salesData,
            sOrderID: salesData.sOrderID,
            orderNo: salesData.documentno,
            orderTime: salesData.orderTime,
            channel: salesData.saleType?.name || "Unknown",
            status: salesData.ofdStatus || "NEW",
            amount: salesData.grosstotal,
            externalId2: salesData?.externalId2 || "",
            customer: salesData?.cwrB2cCustomer?.name || "",
            riderStatus: salesData?.metaData?.find((meta) => meta?.key === "Rider Status")?.value
              ? JSON.parse(salesData?.metaData?.find((meta) => meta?.key === "Rider Status")?.value)?.delivery_info?.rider_status || "Unassigned"
              : salesData?.riderStatus
                ? salesData?.riderStatus
                : "Unassigned",
          }));
        setOrders(newOrders);
        setFilteredOrdersState(newOrders);
        // TSK-4875: never auto-open a cancelled order (no actions on it). Without a
        // tab layout CC isn't listed, so this is newOrders[0] exactly as before.
        const firstOpenOrder = newOrders.find((order) => order.status !== "CC");
        if (!selectedOrder && firstOpenOrder) {
          // BUG-2149: manualprint=true so selectedOrderToPrint is populated on this
          // auto-select too — otherwise it stays null until the order row is manually
          // clicked, and Print would call PrintController(null) in the meantime.
          const data = await getSalesHistoryDetails(firstOpenOrder.sOrderID, firstOpenOrder.status, false, null, true);
          // BUG-2406: only fill an empty selection -- an order clicked while this
          // fetch was in flight must not be replaced by the auto-select.
          if (data?.detailedOrder) setSelectedOrder((prev) => prev || data.detailedOrder);
          if (data?.transformedOrder) setSelectedOrderToPrint((prev) => prev || data.transformedOrder);
        } else if (selectedOrder && !isStatusUpdating) {
          const updatedOrder = newOrders.find((order) => order.sOrderID === selectedOrder.sOrderID);
          if (updatedOrder && updatedOrder.status !== selectedOrder.status) {
            setSelectedOrder((prev) => ({ ...prev, status: updatedOrder.status }));
          }
          // TSK-4875: pick up a bill made elsewhere (another till, Dispatch fallback).
          if (updatedOrder?.isBilled && updatedOrder.isBilled !== selectedOrder.isBilled) {
            setSelectedOrder((prev) => ({ ...prev, isBilled: updatedOrder.isBilled }));
          }
        }
      }
    } catch (error) {
      console.error("Error fetching order history:", error);
      message.error(`${messages.errors.orders.status_fetch_error}`);
    } finally {
      setLoading(false);
    }
  };
  getSalesHistoryDataRef.current = getSalesHistoryData;

  const updateOrderStatus = async (sOrderID, newStatus, isAuto = false, reasonCode = null) => {
    const statusMapping = { Acknowledged: "AK", Cancelled: "CC", "Food Ready": "FR", Dispatched: "DI" };
    const statusCode = statusMapping[newStatus] || newStatus;
    setLoading(true);
    setIsStatusUpdating(true);
    try {
      const response = await upsertOfdOrderStatus(sOrderID, statusCode, null, reasonCode, "N");
      const result = response?.data?.data?.upsertOfdOrderStatus;
      if (result?.status === "200") {
        message.success(isAuto ? "Order automatically accepted after timeout!" : result?.message);
        // BUG-2373: accepting/rejecting/progressing here decides the order just as
        // the popup would, but only the popup used to retire its pending
        // notification. Left unacknowledged, the order was offered again by the
        // 30s poll after the next reload/login and auto-printed a second time.
        acknowledgeAggregatorNotification(sOrderID);
        // BUG-2163: order is finalized (Dispatched) or closed out (Cancelled) --
        // its substitution history no longer needs to persist.
        if (statusCode === "DI" || statusCode === "CC") {
          storage.remove(substitutedLinesKey(sOrderID));
        }
        await getSalesHistoryData();
        if (selectedOrder?.sOrderID === sOrderID) {
          setSelectedOrder((prev) => ({ ...prev, status: statusCode }));
          if (statusCode === "AK") {
            // BUG-2149: unchanged on purpose — forPrinting=true here also triggers the
            // auto-print-on-accept side effect inside getSalesHistoryDetails itself (see
            // its `if (transformedOrder && !manualprint)` block) — passing manualprint
            // would silently stop that auto-print from firing. The resulting selectedOrder/
            // selectedOrderToPrint shape mismatch on Accept is a separate, already-flagged
            // issue, intentionally not changed here.
            //
            // BUG-2322: claim/fetch on selectedOrder.externalId, not sOrderID (the
            // internal id). App.js's handleOrderUpdate -- which also fires for this
            // same accept, since its WS listener is mounted globally regardless of
            // which screen is open -- claims the print-dedup guard (claimPrintOnce)
            // under externalId. Claiming under sOrderID here meant this Aggregator-
            // screen Accept button and the WS order:update broadcast it triggers
            // never actually shared a guard key, so both could print. Safe swap:
            // selectedOrder.sOrderID === sOrderID is guaranteed here (updateOrderStatus
            // is only ever called with selectedOrder?.sOrderID, see the onAccept/
            // onReject/onMarkAsReady/onDispatch wiring below), and the backend's
            // salesHistoryDetails resolver accepts either s_order_id or external_id.
            const orderDetails = await getSalesHistoryDetails(selectedOrder?.externalId || sOrderID, statusCode, true);
            if (orderDetails) setSelectedOrder(orderDetails);
          } else if (statusCode !== "CC") {
            const data = await getSalesHistoryDetails(sOrderID, statusCode, false, null, true);
            if (data?.detailedOrder) setSelectedOrder(data.detailedOrder);
            if (data?.transformedOrder) setSelectedOrderToPrint(data.transformedOrder);
          }
        }
        if (statusCode === "CC" && selectedOrder?.sOrderID === sOrderID) {
          setSelectedOrder(null);
          setSelectedOrderToPrint(null);
        }
      } else {
        message.error(`${messages.errors.orders.status_update_failed}`);
      }
    } catch (error) {
      console.error("Error updating order status:", error);
      message.error(`${messages.errors.orders.status_update_error}`);
    } finally {
      setLoading(false);
      setIsStatusUpdating(false);
    }
  };

  const refreshSelectedOrder = async (sOrderID, status) => {
    const data = await getSalesHistoryDetails(sOrderID, status, false, null, true);
    if (data?.detailedOrder) {
      setSelectedOrder((prev) => (prev?.sOrderID === sOrderID ? data.detailedOrder : prev));
    }
    if (data?.transformedOrder) {
      setSelectedOrderToPrint((prev) => (!prev || prev.sOrderID === sOrderID ? data.transformedOrder : prev));
    }
    return data;
  };

  // TSK-4875: onBill mode. Bills the order (shipment, stock, invoice, receipt)
  // without touching its AK/FR status -- so deliberately no updateOrderStatus
  // (that pushes the status to Swiggy/Zomato) and no syncOrder.
  const handleGenerateBill = async () => {
    if (!selectedOrder?.sOrderID || generatingBill) return;
    const { sOrderID, status } = selectedOrder;
    setGeneratingBill(true);
    try {
      // TIC-10595: don't bill lines the store can't fulfil -- billing them
      // ships them anyway and drives stock negative. Re-checked at click time
      // (not the stockMap loaded when the order was opened, which can be stale
      // and is only loaded with modification on). Removed (qty 0) lines are
      // skipped. A failed check returns an empty map and lets the backend decide.
      const { stockMap: billStockMap } = await checkAggregatorItemsStock(getCurrentTillId(), sOrderID);
      const shortItems = (selectedOrder.items || []).filter((item) => (item.quantity || 0) > 0 && hasStockIssue(item, billStockMap));
      if (shortItems.length > 0) {
        if (allowModification) setStockMap(billStockMap);
        const names = shortItems.map((item) => item.name).join(", ");
        message.error(`Stock not available for ${names} — cannot generate bill.${canModifyItems ? " Modify the item(s) first." : ""}`);
        return;
      }
      const response = await generateAggregatorBill(sOrderID);
      const result = response?.data?.data?.generateAggregatorBill;
      if (result?.status === "200") {
        message.success(result?.message || `Bill generated${result?.documentNo ? `: ${result.documentNo}` : ""}`);
        const data = await refreshSelectedOrder(sOrderID, status);
        // Shipment/invoice status comes from the list query (salesHistoryDetails
        // doesn't return it), so reload the list to pick up the billed values.
        getSalesHistoryData();
        // Print only on a fresh bill -- ALREADY_BILLED (e.g. double-click) already printed.
        if (result?.type === "BILLED" && data?.transformedOrder && !disableAggregatorInvoicePrint) {
          try {
            PrintController(data.transformedOrder);
          } catch (printError) {
            console.error("Error printing aggregator bill:", printError);
            message.error(`${messages.errors.orders.status_print_failed}`);
          }
        }
      } else if (result?.status === "202") {
        // Still generating server-side; retrying now would just return 202 again.
        message.info(result?.message || "Bill is being generated. The order will refresh shortly.");
        setTimeout(() => {
          refreshSelectedOrder(sOrderID, status);
          getSalesHistoryData();
        }, 5000);
      } else {
        message.error(result?.message || "Failed to generate bill.");
      }
    } catch (error) {
      console.error("Error generating aggregator bill:", error);
      message.error("Failed to generate bill.");
    } finally {
      setGeneratingBill(false);
    }
  };

  const syncOrder = async (order) => {
    const tillData = useAuthStore.getState().tillData;
    let storeOpsTillID;
    const tillEvent = await db.tillEvents.where("tillSessionId").equals(tillSession.tillSessionId).first();
    if (tillEvent) storeOpsTillID = tillEvent.storeDailyOpsTillid;
    // BUG-2040: order.tax is a single unvalidated header field (sourced from the
    // backend's own taxamt) that can go stale/₹0 while each line's own tax is
    // correct — self-heal the header from the line sum when they disagree,
    // mirroring dataIntegrity.js's DI-34 self-heal pattern.
    const lineTaxSum = (order.items || []).reduce((sum, item) => sum + (Number(item.tax) || 0), 0);
    const headerTax = Number(order.tax) || 0;
    const resolvedTax = lineTaxSum > 0 && Math.abs(headerTax - lineTaxSum) > 0.01 ? lineTaxSum : headerTax;
    const orderJson = {
      sOrderID: order.sOrderID,
      cSClientID: tillData.tillAccess.csClientId,
      cSBunitID: tillData.tillAccess.csBunit.csBunitId,
      created: order.orderTime,
      createdby: order.createdBy,
      updated: order.orderTime,
      updatedby: order.createdBy,
      csDoctypeId: tillData.tillAccess.csBunit.cwrCsDoctypeId,
      sCustomerId: tillData.tillAccess.csBunit.b2cCustomer.sCustomer.sCustomerID,
      sCustomerBillingId: tillData.tillAccess.csBunit.customerAddress?.sCustomerAddressID,
      sCustomerShippingId: tillData.tillAccess.csBunit.customerAddress?.sCustomerAddressID,
      sPricelistId: tillData.tillAccess.csBunit.cwrSpricelistId,
      documentno: order.orderNo,
      dateordered: order.orderTime,
      datepromised: order.orderTime,
      csPaymenttermID: null,
      finPaymentmethodId: order.paymentMode || null,
      csCurrencyId: tillData?.tillAccess?.csBunit?.currencies[0]?.csCurrencyId || "",
      mWarehouseId: tillData.tillAccess.csBunit.mWarehouse.mWarehouseID,
      cwrLongitude: "",
      cwrLatitude: "",
      csUserId: tillData.tillAccess.csUserId,
      cwrB2cCustomerId: order.customer?.cwrCustomerId || order.customer?.b2cCustomerId,
      orderreference: "",
      cwrPayref: "",
      cwrPayremarks: "",
      description: order?.description || "",
      storeDailyOpsTillid: storeOpsTillID,
      cwrTillId: tillData.tillAccess.cwrTill.cwrTillID,
      redemption: 0,
      accumulation: 0,
      redeemRefId: null,
      roundoff: parseFloat(order?.roundOff?.toFixed(2)) || 0,
      cwrProductQty: order?.totalQty || 0,
      cwrProductCount: order?.totalQty || 0,
      ofdStatus: "Dispatched",
      ofdIspaid: "Y",
      posLoyaltyCalculation: usePOSConfigStore.getState().posConfig?.disablePOSLoyaltyCalculation,
      mPricingruleId: order?.mPricingruleId ?? null,
      cwrSaletypeId: order.channel === "Swiggy" ? "SWIGGY" : order.channel === "Zomato" ? "ZOMATO" : "",
      salesRepId: order?.salesRepId || null,
      discAmount: parseFloat(order.discount?.toFixed(2)) || 0,
      creditAmount: order?.creditAmount,
      metaData: {},
      giftCard: [],
      pricingCoupon: [{ mPricingCouponId: null, redemptionCount: null, referenceId: null, couponCode: null }],
      orderTotal: parseFloat(order.amount?.toFixed(2)) || 0,
      nettotal: parseFloat((order.amount - resolvedTax).toFixed(2)) || 0,
      taxamt: parseFloat(resolvedTax.toFixed(2)) || 0,
      isReturn: "N",
      sOrderReturnId: order?.items?.[0]?.sOrderReturnId || null,
      layAway: order.layAway || null,
      payments: order.paymentMode ? [{ paymentMethod: order.paymentMode, amount: order.amount }] : [],
      tax: resolvedTax ? [{ amount: resolvedTax }] : [],
      line:
        order.items?.map((item) => ({
          productName: item.name,
          qty: item.quantity,
          price: item.price,
          discount: item.discount,
          tax: item.tax,
        })) || [],
    };
    const stringifiedData = JSON.stringify(orderJson);
    const newStringRequest = JSON.stringify(stringifiedData.replace(/\\"/g, '\\"'));
    const paramsInput = {
      query: `
        mutation {
          posOrderProcessor1(
            tillId: "${tillData.tillAccess.cwrTill.cwrTillID}",
            orderId: "${order.sOrderID}",
            documentNo: "${order.orderNo}",
            orderJson: ${newStringRequest}
          ) {
            documentno
            status
            message
          }
        }
      `,
    };
    try {
      const response = await posOrderProcessor1(paramsInput);
      const result = response?.data?.data?.posOrderProcessor1;
      if (result?.status === "200") {
        message.success(`Order ${result.documentno} synced successfully!`);
      } else {
        message.error(`${messages.errors.orders.status_sync_failed}`);
      }
    } catch (error) {
      console.error("Error syncing order:", error);
      message.error(`${messages.errors.orders.status_sync_error}`);
    }
  };

  const handleOrderClick = (order) => {
    getSalesHistoryDetails(order.sOrderID, order.status, false, null, true).then((data) => {
      if (data) {
        const { detailedOrder, transformedOrder } = data;
        if (detailedOrder) {
          setSelectedOrder(detailedOrder);
          setSelectedOrderToPrint(transformedOrder);
        }
      }
    });
  };

  const handlePrint = () => {
    if (selectedOrder || selectedOrderToPrint) {
      try {
        // BUG-1971: invoice and KOT are independent outputs, gated by their own flags --
        // an if/else-if here made them mutually exclusive, so with both flags at their
        // default (false = enabled), only the invoice printed and KOT never did.
        if (!disableAggregatorInvoicePrint) {
          PrintController(selectedOrderToPrint);
        }
        if (!disableAggregatorKOTPrint) {
          CWKOTPrinter(selectedOrderToPrint, true);
        }
      } catch (error) {
        console.error("Error printing order:", error);
        message.error(`${messages.errors.orders.status_print_failed}`);
      }
    } else {
      message.warning("Please select an order to print.");
    }
  };

  const rejectReasons = [
    "item_out_of_stock",
    "store_closed",
    "store_busy",
    "rider_not_available",
    "out_of_delivery_radius",
    "connectivity_issue",
    "total_missmatch",
    "invalid_item",
    "option_out_of_stock",
    "invalid_option",
    "unspecified",
  ];

  // TSK-4875: AK and FR (up to Dispatch), hidden only once billed. Keyed on
  // "Y" rather than "N" so an order object that lacks the field (a status
  // change refresh, a list-row merge) still shows the button -- the backend
  // answers a repeat bill with ALREADY_BILLED, never a second invoice.
  const showGenerateBill = shipmentMode === "onBill" && ["AK", "FR"].includes(selectedOrder?.status) && selectedOrder?.isBilled !== "Y";
  // TSK-4875: fully out-of-stock lines only (not Partial Stock) -- drives the
  // wireframe 11b banner and the Generate Bill hint.
  const outOfStockCount = allowModification ? (selectedOrder?.items || []).filter((item) => isItemOutOfStock(item, stockMap)).length : 0;
  // TSK-4875 (wireframe 11d): onAccept bills on Accept, so out-of-stock lines
  // have to be fixed while the order is still NEW.
  const billsOnAccept = shipmentMode === "onAccept" && selectedOrder?.status === "NEW";
  const canModifyItems = allowModification && orderModifiable === "Y" && selectedOrder?.isBilled !== "Y" && modifyStatuses.includes(selectedOrder?.status);
  // s_order.shipmentstatus / invoicestatus are returned by the list query only
  // (salesHistoryDetails leaves them null), so read them off the list row.
  const selectedListOrder = orders.find((order) => order.sOrderID === selectedOrder?.sOrderID);

  return (
    <div style={{ height: "100%", backgroundColor: "#f3f4f9", padding: "8px" }}>
      <Header currentDateTime={currentDateTime} tillValue={tillValue} onBack={handleBack} onPosClick={handleBack} />
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)", gap: "12px" }}>
        <OrderList
          orders={filteredOrdersState} // Pass filteredOrdersState instead of orders
          selectedChannel={selectedChannel}
          setSelectedChannel={setSelectedChannel}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          loading={loading}
          onOrderClick={handleOrderClick}
          onRefresh={getSalesHistoryData}
          statusDisplayMapping={statusDisplayMapping}
          oosOrderIds={allowModification ? oosOrderIds : undefined}
          tabLayout={tabLayout}
        />
        <OrderDetails
          selectedOrder={selectedOrder}
          statusDisplayMapping={statusDisplayMapping}
          onPrint={handlePrint}
          stockMap={allowModification ? stockMap : undefined}
          shipmentMode={shipmentMode}
          shipmentStatus={selectedListOrder?.shipmentStatus}
          orderInvoiceStatus={selectedListOrder?.orderInvoiceStatus}
          showOutOfStockBanner={(showGenerateBill || billsOnAccept) && outOfStockCount > 0}
          outOfStockBeforeAccept={billsOnAccept}
          outOfStockCount={outOfStockCount}
          actionButtons={
            <ActionButtons
              selectedOrder={selectedOrder}
              loading={loading}
              onAccept={() => updateOrderStatus(selectedOrder?.sOrderID, "Acknowledged")}
              onReject={(reason) => updateOrderStatus(selectedOrder?.sOrderID, "Cancelled", false, reason)}
              onMarkAsReady={() => updateOrderStatus(selectedOrder?.sOrderID, "Food Ready")}
              onComplete={() => {
                updateOrderStatus(selectedOrder?.sOrderID, "Dispatched").then(() => syncOrder(selectedOrder));
              }}
              onModifyItems={handleModifyItems}
              // TSK-4875: per-mode status window, and never once billed.
              allowModification={canModifyItems}
              billsOnAccept={billsOnAccept}
              showBilledHint={shipmentMode === "onBill" && selectedOrder?.isBilled === "Y"}
              showGenerateBill={showGenerateBill}
              hasOutOfStock={outOfStockCount > 0}
              generatingBill={generatingBill}
              onGenerateBill={handleGenerateBill}
              rejectReasons={rejectReasons}
            />
          }
        />
      </div>
      {allowModification && (
        <ModifyItemsModal
          visible={modifyItemsVisible}
          order={selectedOrder}
          stockMap={stockMap}
          substitutedLineIds={substitutedLineIds}
          tillId={getCurrentTillId()}
          onClose={() => setModifyItemsVisible(false)}
          onConfirm={handleConfirmItemModification}
          confirming={confirmingModify}
        />
      )}
    </div>
  );
};

export default Aggregator;
